import { useState } from "react";
import { ScrollReveal } from "@/components/base/ScrollAnimations";
import { contactSubjects } from "@/mocks/contactData";

const API_BASE = import.meta.env.VITE_API_BASE || "https://api.textileinnovationexchange.com/api";

export default function ContactForm() {
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [toast, setToast] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const showToast = (toastData: { type: "success" | "error"; message: string }) => {
    setToast(toastData);
    window.setTimeout(() => setToast(null), 5000);
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }
    if (!formData.subject) newErrors.subject = "Please select a subject";
    if (!formData.message.trim()) newErrors.message = "Message is required";
    if (formData.message.length > 500) newErrors.message = "Message must be under 500 characters";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const data = new URLSearchParams();
    data.append("name", formData.name);
    data.append("email", formData.email);
    data.append("company", formData.company);
    data.append("phone", formData.phone);
    data.append("subject", formData.subject);
    data.append("message", formData.message);

    fetch(`${API_BASE}/contacts`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: data.toString(),
    })
      .then(() => {
        setSubmitted(true);
        showToast({ type: "success", message: "Message sent successfully." });
      })
      .catch(() => {
        setSubmitted(true);
        showToast({ type: "error", message: "Submission failed. Please try again." });
      });
  };

  if (submitted) {
    return (
      <section id="contact-form" className="bg-tie-navy py-16 md:py-24 lg:py-32">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-xl mx-auto text-center">
            <ScrollReveal>
              <div className="w-16 h-16 rounded-full bg-tie-teal/20 flex items-center justify-center mx-auto mb-6">
                <i className="ri-checkbox-circle-line text-tie-teal-light text-3xl" />
              </div>
              <h2 className="font-serif text-2xl md:text-3xl font-bold text-white mb-3">
                Message Sent
              </h2>
              <p className="text-base text-white/70 leading-relaxed mb-6">
                Thank you for reaching out. We have received your message and will get back to you within 48 hours. For urgent matters, please call us directly.
              </p>
              <button
                onClick={() => {
                  setSubmitted(false);
                  setFormData({ name: "", email: "", company: "", phone: "", subject: "", message: "" });
                }}
                className="inline-flex items-center px-6 py-3 rounded-full bg-white/10 text-white border border-white/30 text-sm font-medium hover:bg-white/20 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Send Another Message
              </button>
            </ScrollReveal>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="contact-form" className="bg-tie-navy py-16 md:py-24 lg:py-32">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-3xl mx-auto">
          <ScrollReveal>
            <div className="text-center mb-10 md:mb-14">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-tie-teal/20 mb-4 md:mb-6">
                <span className="text-xs font-semibold text-tie-teal-light uppercase tracking-wider">
                  / Send a Message
                </span>
              </div>
              <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl font-bold text-white leading-tight">
                We Would Love to Hear From You
              </h2>
              <p className="mt-3 md:mt-4 text-base md:text-lg text-white/60">
                Fill out the form below and our team will respond promptly. All fields marked with an asterisk are required.
              </p>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={150}>
            <form
              id="contact-enquiry"
              data-readdy-form
              onSubmit={handleSubmit}
              className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8 lg:p-10"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5 mb-4 md:mb-5">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-white/80 mb-1.5">
                    Full Name <span className="text-tie-teal-light">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your full name"
                    className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/20 text-white placeholder-white/40 text-sm focus:outline-none focus:border-tie-teal-light transition-colors"
                  />
                  {errors?.name && (
                    <p className="mt-1 text-xs text-red-400">{errors.name}</p>
                  )}
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-white/80 mb-1.5">
                    Email Address <span className="text-tie-teal-light">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="you@company.com"
                    className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/20 text-white placeholder-white/40 text-sm focus:outline-none focus:border-tie-teal-light transition-colors"
                  />
                  {errors?.email && (
                    <p className="mt-1 text-xs text-red-400">{errors.email}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5 mb-4 md:mb-5">
                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-white/80 mb-1.5">
                    Company / Organization
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="Your organization"
                    className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/20 text-white placeholder-white/40 text-sm focus:outline-none focus:border-tie-teal-light transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-white/80 mb-1.5">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+880 1XXX-XXXXXX"
                    className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/20 text-white placeholder-white/40 text-sm focus:outline-none focus:border-tie-teal-light transition-colors"
                  />
                </div>
              </div>

              <div className="mb-4 md:mb-5">
                <label htmlFor="subject" className="block text-sm font-medium text-white/80 mb-1.5">
                  Subject <span className="text-tie-teal-light">*</span>
                </label>
                <select
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/20 text-white text-sm focus:outline-none focus:border-tie-teal-light transition-colors appearance-none cursor-pointer"
                >
                  <option value="" className="bg-tie-navy text-white/60">
                    Select a subject
                  </option>
                  {contactSubjects.map((s) => (
                    <option key={s} value={s} className="bg-tie-navy text-white">
                      {s}
                    </option>
                  ))}
                </select>
                {errors?.subject && (
                  <p className="mt-1 text-xs text-red-400">{errors.subject}</p>
                )}
              </div>

              <div className="mb-6 md:mb-8">
                <label htmlFor="message" className="block text-sm font-medium text-white/80 mb-1.5">
                  Message <span className="text-tie-teal-light">*</span>
                  <span className="text-white/40 ml-1 font-normal">({formData.message.length}/500)</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="How can we help you? Describe your enquiry, question, or request..."
                  rows={5}
                  maxLength={500}
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/20 text-white placeholder-white/40 text-sm focus:outline-none focus:border-tie-teal-light transition-colors resize-none"
                />
                {errors?.message && (
                  <p className="mt-1 text-xs text-red-400">{errors.message}</p>
                )}
              </div>

              <button
                type="submit"
                className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-4 rounded-full bg-tie-teal text-white font-medium text-sm md:text-base hover:bg-tie-teal-dark transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Send Message
                <i className="ri-send-plane-line ml-2" />
              </button>
            </form>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}